import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../components/ConfirmDialog';
import toast from 'react-hot-toast';

// ── Shared helpers ────────────────────────────────────────────────────────────

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtDateTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, icon, sub }) {
  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1.25rem 1.5rem', flex: '1 1 160px', minWidth: 140 }}>
      <span style={{ fontSize: '1.5rem' }}>{icon}</span>
      <div style={{ fontSize: '2rem', fontWeight: 700, color: '#111827', lineHeight: 1.1, marginTop: 4 }}>{value ?? '—'}</div>
      <div style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 500, marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── Pagination ────────────────────────────────────────────────────────────────

function Pagination({ page, pages, onPage }) {
  if (pages <= 1) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: '1rem', justifyContent: 'flex-end' }}>
      <button disabled={page <= 1} onClick={() => onPage(page - 1)}
        style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: page <= 1 ? '#f9fafb' : 'white', cursor: page <= 1 ? 'default' : 'pointer', color: page <= 1 ? '#9ca3af' : '#374151', fontSize: '0.875rem' }}>
        Prev
      </button>
      <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>Page {page} of {pages}</span>
      <button disabled={page >= pages} onClick={() => onPage(page + 1)}
        style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: page >= pages ? '#f9fafb' : 'white', cursor: page >= pages ? 'default' : 'pointer', color: page >= pages ? '#9ca3af' : '#374151', fontSize: '0.875rem' }}>
        Next
      </button>
    </div>
  );
}

// ── Shared table components ───────────────────────────────────────────────────

function TableWrap({ children }) {
  return (
    <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #e5e7eb' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>{children}</table>
    </div>
  );
}

function Th({ children, style }) {
  return <th style={{ padding: '10px 14px', background: '#f9fafb', color: '#6b7280', fontWeight: 600, textAlign: 'left', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap', ...style }}>{children}</th>;
}

function Td({ children, style }) {
  return <td style={{ padding: '10px 14px', borderBottom: '1px solid #f3f4f6', color: '#374151', verticalAlign: 'middle', ...style }}>{children}</td>;
}

function SearchBar({ value, onChange, placeholder, onSearch }) {
  return (
    <div style={{ display: 'flex', gap: 8, flex: 1 }}>
      <input value={value} onChange={e => onChange(e.target.value)} onKeyDown={e => e.key === 'Enter' && onSearch()}
        placeholder={placeholder}
        style={{ flex: 1, padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem', outline: 'none' }} />
      <button onClick={onSearch}
        style={{ padding: '7px 16px', background: '#0D9488', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500 }}>
        Search
      </button>
    </div>
  );
}


const statusBadge = (s) => {
  const colors = { draft: ['#f3f4f6', '#6b7280'], registration: ['#dcfce7', '#15803d'], seeding: ['#fef9c3', '#92400e'], in_progress: ['#dbeafe', '#1d4ed8'], completed: ['#CCFBF1', '#6d28d9'], cancelled: ['#fee2e2', '#b91c1c'] };
  const [bg, fg] = colors[s] || ['#f3f4f6', '#6b7280'];
  return <span style={{ background: bg, color: fg, borderRadius: 6, padding: '2px 8px', fontSize: '0.75rem', fontWeight: 600 }}>{s?.replace(/_/g, ' ')}</span>;
};

// ── Tab 1: Overview ───────────────────────────────────────────────────────────

function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getStats()
      .then(r => setStats(r.data))
      .catch(() => toast.error('Failed to load stats'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p style={{ color: '#9ca3af', padding: '2rem' }}>Loading…</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Users */}
      <div>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.75rem' }}>Users</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
          <StatCard label="Total Users"       value={stats?.total_users}     icon="👤" />
          <StatCard label="Organisers/Admins" value={stats?.organizers}      icon="🏆" />
          <StatCard label="New This Month"    value={stats?.new_users_month}  icon="✨" />
        </div>
      </div>

      {/* Tournaments */}
      <div>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.75rem' }}>Tournaments</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
          <StatCard label="Total"         value={stats?.total_tournaments}   icon="📋" />
          <StatCard label="Active Now"    value={stats?.active_tournaments}  icon="⚡" />
          <StatCard label="Upcoming"      value={stats?.upcoming_tournaments} icon="📅" />
          <StatCard label="This Month"    value={stats?.tournaments_month}   icon="📈" />
        </div>
      </div>

      {/* Platform */}
      <div>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.75rem' }}>Platform</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
          <StatCard label="Total Matches"  value={stats?.total_matches}   icon="🎯" />
          <StatCard label="Player Entries" value={stats?.total_players}   icon="🏸" />
        </div>
      </div>

      {/* Subscriptions */}
      <div>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.75rem' }}>Subscriptions</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
          <StatCard label="Active"    value={stats?.sub_active}    icon="✅" />
          <StatCard label="Free"      value={stats?.sub_free}      icon="🆓" />
          <StatCard label="Expired"   value={stats?.sub_expired}   icon="⏰" />
          <StatCard label="Cancelled" value={stats?.sub_cancelled} icon="❌" />
        </div>
      </div>

      {/* Sport distribution */}
      {stats?.sport_distribution?.length > 0 && (
        <div>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.75rem' }}>Sport Distribution</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {stats.sport_distribution.map(s => (
              <div key={s.sport_type} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.5rem 1rem', display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontWeight: 600, color: '#111827', textTransform: 'capitalize' }}>{s.sport_type}</span>
                <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab 2: Users ──────────────────────────────────────────────────────────────

function UsersTab() {
  const { user: me } = useAuth();
  const confirm = useConfirm();

  const [q, setQ]         = useState('');
  const [role, setRole]   = useState('');
  const [page, setPage]   = useState(1);
  const [data, setData]   = useState({ users: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [editSub, setEditSub] = useState({}); // { [userId]: { plan, start, end } }

  const load = useCallback((pg = 1) => {
    setLoading(true);
    adminApi.getUsers({ q, role, page: pg })
      .then(r => { setData(r.data); setPage(pg); })
      .catch(() => toast.error('Failed to load users'))
      .finally(() => setLoading(false));
  }, [q, role]);

  useEffect(() => { load(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRoleChange = async (userId, newRole) => {
    try {
      await adminApi.updateUser(userId, { role: newRole });
      toast.success('Role updated');
      setData(prev => ({ ...prev, users: prev.users.map(u => u.id === userId ? { ...u, role: newRole } : u) }));
    } catch { toast.error('Failed to update role'); }
  };

  const handleSubChange = async (userId, newSub) => {
    try {
      await adminApi.updateUser(userId, { subscription_status: newSub });
      toast.success('Subscription updated');
      setData(prev => ({ ...prev, users: prev.users.map(u => u.id === userId ? { ...u, subscription_status: newSub } : u) }));
    } catch { toast.error('Failed to update subscription'); }
  };

  const handleSubDetails = async (userId) => {
    const ed = editSub[userId];
    if (!ed) return;
    try {
      const updated = await adminApi.updateUser(userId, {
        subscription_plan:  ed.plan  || null,
        subscription_start: ed.start || null,
        subscription_end:   ed.end   || null,
      });
      toast.success('Subscription details saved');
      setData(prev => ({ ...prev, users: prev.users.map(u => u.id === userId ? updated.data : u) }));
      setEditSub(prev => { const n = { ...prev }; delete n[userId]; return n; });
    } catch { toast.error('Failed to save subscription details'); }
  };

  const handleDelete = async (userId, userName) => {
    const ok = await confirm({ title: 'Delete user?', message: `Permanently delete "${userName}"? This cannot be undone.`, confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    try {
      await adminApi.deleteUser(userId);
      toast.success('User deleted');
      load(page);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to delete user'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: '1rem', flexWrap: 'wrap' }}>
        <SearchBar value={q} onChange={setQ} placeholder="Search name or email…" onSearch={() => load(1)} />
        <select value={role} onChange={e => setRole(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem' }}>
          <option value="">All roles</option>
          <option value="player">Player</option>
          <option value="moderator">Moderator</option>
          <option value="organizer">Organizer</option>
          <option value="admin">Admin</option>
        </select>
      </div>

      {loading ? <p style={{ color: '#9ca3af' }}>Loading…</p> : (
        <>
          <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem' }}>{data.total} user{data.total !== 1 ? 's' : ''}</p>
          <TableWrap>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Subscription</Th>
                <Th>Plan / Expiry</Th>
                <Th>Joined</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {data.users.length === 0 && (
                <tr><td colSpan={7} style={{ padding: '1.5rem', textAlign: 'center', color: '#9ca3af' }}>No users found</td></tr>
              )}
              {data.users.map(u => {
                const ed = editSub[u.id];
                return (
                  <tr key={u.id} style={{ background: u.id === me?.id ? '#fafaf9' : undefined }}>
                    <Td style={{ fontWeight: 500 }}>{u.name}</Td>
                    <Td style={{ color: '#6b7280' }}>{u.email}</Td>
                    <Td>
                      <select value={u.role} onChange={e => handleRoleChange(u.id, e.target.value)}
                        style={{ padding: '3px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.8rem', background: 'white' }}>
                        <option value="player">Player</option>
                        <option value="moderator">Moderator</option>
                        <option value="organizer">Organizer</option>
                        <option value="admin">Admin</option>
                      </select>
                    </Td>
                    <Td>
                      <select value={u.subscription_status || 'free'} onChange={e => handleSubChange(u.id, e.target.value)}
                        style={{ padding: '3px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.8rem', background: 'white' }}>
                        <option value="free">Free</option>
                        <option value="active">Active</option>
                        <option value="expired">Expired</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </Td>
                    <Td style={{ minWidth: 220 }}>
                      {ed ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <input placeholder="Plan name" value={ed.plan || ''} onChange={e => setEditSub(p => ({ ...p, [u.id]: { ...p[u.id], plan: e.target.value } }))}
                            style={{ padding: '2px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.78rem', width: '100%' }} />
                          <div style={{ display: 'flex', gap: 4 }}>
                            <input type="date" value={ed.start || ''} onChange={e => setEditSub(p => ({ ...p, [u.id]: { ...p[u.id], start: e.target.value } }))}
                              style={{ padding: '2px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.78rem', flex: 1 }} />
                            <input type="date" value={ed.end || ''} onChange={e => setEditSub(p => ({ ...p, [u.id]: { ...p[u.id], end: e.target.value } }))}
                              style={{ padding: '2px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.78rem', flex: 1 }} />
                          </div>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button onClick={() => handleSubDetails(u.id)}
                              style={{ padding: '2px 8px', background: '#0D9488', color: 'white', border: 'none', borderRadius: 4, fontSize: '0.75rem', cursor: 'pointer' }}>Save</button>
                            <button onClick={() => setEditSub(p => { const n = { ...p }; delete n[u.id]; return n; })}
                              style={{ padding: '2px 8px', background: 'none', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: '0.75rem', cursor: 'pointer', color: '#6b7280' }}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <span style={{ fontSize: '0.8rem', color: '#374151' }}>{u.subscription_plan || <span style={{ color: '#d1d5db' }}>—</span>}</span>
                          {u.subscription_end && <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>until {fmt(u.subscription_end)}</span>}
                          <button onClick={() => setEditSub(p => ({ ...p, [u.id]: { plan: u.subscription_plan || '', start: u.subscription_start?.slice(0,10) || '', end: u.subscription_end?.slice(0,10) || '' } }))}
                            style={{ padding: '2px 8px', background: 'none', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: '0.72rem', cursor: 'pointer', color: '#6b7280', alignSelf: 'flex-start', marginTop: 2 }}>
                            Edit plan
                          </button>
                        </div>
                      )}
                    </Td>
                    <Td style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{fmt(u.created_at)}</Td>
                    <Td>
                      <button disabled={u.id === me?.id} onClick={() => handleDelete(u.id, u.name)}
                        title={u.id === me?.id ? 'Cannot delete your own account' : 'Delete user'}
                        style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #fca5a5', background: u.id === me?.id ? '#f9fafb' : '#fef2f2', color: u.id === me?.id ? '#9ca3af' : '#dc2626', cursor: u.id === me?.id ? 'default' : 'pointer', fontSize: '0.8rem', fontWeight: 500 }}>
                        Delete
                      </button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </TableWrap>
          <Pagination page={page} pages={data.pages} onPage={p => load(p)} />
        </>
      )}
    </div>
  );
}

// ── Tab 3: Tournaments ────────────────────────────────────────────────────────

function TournamentsTab() {
  const confirm = useConfirm();

  const [q, setQ]           = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage]     = useState(1);
  const [data, setData]     = useState({ tournaments: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(false);

  // Create-on-behalf modal state
  const [showCreate, setShowCreate] = useState(false);
  const [organizers, setOrganizers] = useState([]);
  const [createForm, setCreateForm] = useState({ name: '', sport_type: 'badminton', draw_type: 'knockout', on_behalf_of: '', start_date: '', end_date: '', venue: '' });
  const [creating, setCreating] = useState(false);

  const load = useCallback((pg = 1) => {
    setLoading(true);
    adminApi.getTournaments({ q, status, page: pg })
      .then(r => { setData(r.data); setPage(pg); })
      .catch(() => toast.error('Failed to load tournaments'))
      .finally(() => setLoading(false));
  }, [q, status]);

  useEffect(() => { load(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = async () => {
    try {
      const res = await adminApi.getOrganizers();
      setOrganizers(res.data);
      setCreateForm({ name: '', sport_type: 'badminton', draw_type: 'knockout', on_behalf_of: res.data[0]?.id || '', start_date: '', end_date: '', venue: '' });
      setShowCreate(true);
    } catch { toast.error('Failed to load organizers'); }
  };

  const handleCreate = async () => {
    if (!createForm.name.trim()) { toast.error('Tournament name is required'); return; }
    if (!createForm.on_behalf_of) { toast.error('Select an organizer'); return; }
    setCreating(true);
    try {
      await adminApi.createTournament(createForm);
      toast.success('Tournament created');
      setShowCreate(false);
      load(1);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to create tournament'); }
    finally { setCreating(false); }
  };

  const handleDelete = async (tid, tname) => {
    const ok = await confirm({ title: 'Delete tournament?', message: `Permanently delete "${tname}"? This will also delete all matches, brackets, players, and schedules inside it. This cannot be undone.`, confirmLabel: 'Delete tournament', danger: true });
    if (!ok) return;
    try {
      await adminApi.deleteTournament(tid);
      toast.success('Tournament deleted');
      load(page);
    } catch { toast.error('Failed to delete tournament'); }
  };

  const handleToggleManualDraw = async (tid, current) => {
    const enabling = !current;
    // Optimistic update
    setData(prev => prev ? ({
      ...prev,
      tournaments: prev.tournaments.map(t =>
        t.id === tid ? { ...t, manual_draw_enabled: enabling } : t
      ),
    }) : prev);
    try {
      await adminApi.setManualDrawPermission(tid, enabling);
      toast.success(enabling ? 'Manual draw enabled' : 'Manual draw revoked');
    } catch {
      // Revert on failure
      setData(prev => prev ? ({
        ...prev,
        tournaments: prev.tournaments.map(t =>
          t.id === tid ? { ...t, manual_draw_enabled: current } : t
        ),
      }) : prev);
      toast.error('Failed to update manual draw permission');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <SearchBar value={q} onChange={setQ} placeholder="Search tournament name…" onSearch={() => load(1)} />
        <select value={status} onChange={e => setStatus(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem' }}>
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="registration">Registration</option>
          <option value="seeding">Seeding</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button onClick={openCreate}
          style={{ padding: '7px 16px', background: '#0D9488', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
          + Create for Organizer
        </button>
      </div>

      {/* Create-on-behalf modal */}
      {showCreate && (
        <div style={{ background: '#f8fafc', border: '1px solid #99F6E4', borderRadius: 10, padding: '1.25rem', marginBottom: '1rem' }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#115E59', marginBottom: '1rem' }}>Create Tournament on Behalf of Organizer</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <label style={{ fontSize: '0.78rem', color: '#6b7280', display: 'block', marginBottom: 3 }}>Tournament Name *</label>
              <input value={createForm.name} onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Gurugram Open 2026"
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.875rem', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: '0.78rem', color: '#6b7280', display: 'block', marginBottom: 3 }}>Organizer *</label>
              <select value={createForm.on_behalf_of} onChange={e => setCreateForm(p => ({ ...p, on_behalf_of: e.target.value }))}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.875rem' }}>
                <option value="">— Select organizer —</option>
                {organizers.map(o => <option key={o.id} value={o.id}>{o.name} ({o.email})</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.78rem', color: '#6b7280', display: 'block', marginBottom: 3 }}>Sport</label>
              <select value={createForm.sport_type} onChange={e => setCreateForm(p => ({ ...p, sport_type: e.target.value }))}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.875rem' }}>
                {['badminton','tennis','table_tennis','squash','pickleball','padel'].map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.78rem', color: '#6b7280', display: 'block', marginBottom: 3 }}>Draw Type</label>
              <select value={createForm.draw_type} onChange={e => setCreateForm(p => ({ ...p, draw_type: e.target.value }))}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.875rem' }}>
                <option value="knockout">Knockout</option>
                <option value="league">League</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.78rem', color: '#6b7280', display: 'block', marginBottom: 3 }}>Start Date</label>
              <input type="date" value={createForm.start_date} onChange={e => setCreateForm(p => ({ ...p, start_date: e.target.value }))}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.875rem', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: '0.78rem', color: '#6b7280', display: 'block', marginBottom: 3 }}>Venue</label>
              <input value={createForm.venue} onChange={e => setCreateForm(p => ({ ...p, venue: e.target.value }))} placeholder="Optional"
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.875rem', boxSizing: 'border-box' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleCreate} disabled={creating}
              style={{ padding: '7px 18px', background: '#0D9488', color: 'white', border: 'none', borderRadius: 7, cursor: creating ? 'default' : 'pointer', fontSize: '0.875rem', fontWeight: 600, opacity: creating ? 0.7 : 1 }}>
              {creating ? 'Creating…' : 'Create Tournament'}
            </button>
            <button onClick={() => setShowCreate(false)}
              style={{ padding: '7px 14px', background: 'none', border: '1px solid #e5e7eb', borderRadius: 7, cursor: 'pointer', fontSize: '0.875rem', color: '#6b7280' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? <p style={{ color: '#9ca3af' }}>Loading…</p> : (
        <>
          <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem' }}>{data.total} tournament{data.total !== 1 ? 's' : ''}</p>
          <TableWrap>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Creator</Th>
                <Th>Sport</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {data.tournaments.length === 0 && (
                <tr><td colSpan={6} style={{ padding: '1.5rem', textAlign: 'center', color: '#9ca3af' }}>No tournaments found</td></tr>
              )}
              {data.tournaments.map(t => (
                <tr key={t.id}>
                  <Td style={{ fontWeight: 500, maxWidth: 200 }}>{t.name}</Td>
                  <Td style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                    <div>{t.creator_name || '—'}</div>
                    {t.creator_email && <div style={{ color: '#9ca3af' }}>{t.creator_email}</div>}
                  </Td>
                  <Td style={{ color: '#6b7280', textTransform: 'capitalize' }}>{t.sport_type || '—'}</Td>
                  <Td>{statusBadge(t.status)}</Td>
                  <Td style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{fmt(t.created_at)}</Td>
                  <Td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Link to={`/tournaments/${t.id}`} target="_blank"
                        style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #99F6E4', background: '#F0FDFA', color: '#0D9488', fontSize: '0.8rem', fontWeight: 500, textDecoration: 'none' }}>
                        View
                      </Link>
                      <label title={t.manual_draw_enabled ? 'Revoke manual draw' : 'Enable manual draw'}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleToggleManualDraw(t.id, t.manual_draw_enabled)}>
                        {/* Toggle track */}
                        <span style={{
                          display: 'inline-block', width: 32, height: 18, borderRadius: 9, position: 'relative',
                          background: t.manual_draw_enabled ? '#0D9488' : '#d1d5db',
                          transition: 'background 0.2s', flexShrink: 0,
                        }}>
                          {/* Knob */}
                          <span style={{
                            position: 'absolute', top: 2, left: t.manual_draw_enabled ? 16 : 2,
                            width: 14, height: 14, borderRadius: '50%', background: 'white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s',
                          }} />
                        </span>
                        <span style={{ fontSize: '0.78rem', color: t.manual_draw_enabled ? '#065f46' : '#9ca3af', fontWeight: 500 }}>
                          Manual Draw
                        </span>
                      </label>
                      <button onClick={() => handleDelete(t.id, t.name)}
                        style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500 }}>
                        Delete
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <Pagination page={page} pages={data.pages} onPage={p => load(p)} />
        </>
      )}
    </div>
  );
}

// ── Tab 4: Players ────────────────────────────────────────────────────────────

function PlayersTab() {
  const confirm = useConfirm();

  const [q, setQ]           = useState('');
  const [linked, setLinked] = useState('');
  const [page, setPage]     = useState(1);
  const [data, setData]     = useState({ players: [], total: 0, pages: 1 });
  const [loading, setLoading]     = useState(false);
  const [linkInputs, setLinkInputs] = useState({});
  const [linking, setLinking]       = useState({});

  const load = useCallback((pg = 1) => {
    setLoading(true);
    adminApi.getPlayers({ q, linked, page: pg })
      .then(r => { setData(r.data); setPage(pg); })
      .catch(() => toast.error('Failed to load players'))
      .finally(() => setLoading(false));
  }, [q, linked]);

  useEffect(() => { load(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLink = async (playerId) => {
    const email = (linkInputs[playerId] || '').trim();
    if (!email) return;
    setLinking(prev => ({ ...prev, [playerId]: true }));
    try {
      const res = await adminApi.linkPlayer(playerId, email);
      toast.success(`Linked to ${res.data.linked_email}`);
      setData(prev => ({ ...prev, players: prev.players.map(p => p.id === playerId ? { ...p, user_id: '1', linked_email: res.data.linked_email, linked_name: res.data.linked_name } : p) }));
      setLinkInputs(prev => { const n = { ...prev }; delete n[playerId]; return n; });
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to link player'); }
    finally { setLinking(prev => ({ ...prev, [playerId]: false })); }
  };

  const handleUnlink = async (playerId, playerName) => {
    const ok = await confirm({ title: 'Unlink player?', message: `Remove the user account link from "${playerName}"?`, confirmLabel: 'Unlink', danger: false });
    if (!ok) return;
    try {
      await adminApi.unlinkPlayer(playerId);
      toast.success('Player unlinked');
      setData(prev => ({ ...prev, players: prev.players.map(p => p.id === playerId ? { ...p, user_id: null, linked_email: null, linked_name: null } : p) }));
    } catch { toast.error('Failed to unlink player'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: '1rem', flexWrap: 'wrap' }}>
        <SearchBar value={q} onChange={setQ} placeholder="Search player name or email…" onSearch={() => load(1)} />
        <select value={linked} onChange={e => setLinked(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem' }}>
          <option value="">All players</option>
          <option value="yes">Linked</option>
          <option value="no">Unlinked</option>
        </select>
      </div>

      {loading ? <p style={{ color: '#9ca3af' }}>Loading…</p> : (
        <>
          <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem' }}>{data.total} player{data.total !== 1 ? 's' : ''}</p>
          <TableWrap>
            <thead>
              <tr>
                <Th>Player Name</Th>
                <Th>Email</Th>
                <Th>Tournament</Th>
                <Th>Linked User</Th>
                <Th style={{ minWidth: 220 }}>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {data.players.length === 0 && (
                <tr><td colSpan={5} style={{ padding: '1.5rem', textAlign: 'center', color: '#9ca3af' }}>No players found</td></tr>
              )}
              {data.players.map(p => (
                <tr key={p.id}>
                  <Td style={{ fontWeight: 500 }}>{p.player_name}</Td>
                  <Td style={{ color: '#6b7280', fontSize: '0.8rem' }}>{p.player_email || '—'}</Td>
                  <Td style={{ color: '#6b7280', fontSize: '0.8rem', maxWidth: 160 }}>{p.tournament_name || '—'}</Td>
                  <Td>{p.linked_email ? <span style={{ color: '#15803d', fontSize: '0.8rem', fontWeight: 500 }}>{p.linked_email}</span> : <span style={{ color: '#d1d5db', fontSize: '0.8rem' }}>—</span>}</Td>
                  <Td>
                    {p.user_id ? (
                      <button onClick={() => handleUnlink(p.id, p.player_name)}
                        style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#f9fafb', color: '#374151', cursor: 'pointer', fontSize: '0.8rem' }}>
                        Unlink
                      </button>
                    ) : (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input placeholder="User email" value={linkInputs[p.id] || ''} onChange={e => setLinkInputs(prev => ({ ...prev, [p.id]: e.target.value }))} onKeyDown={e => e.key === 'Enter' && handleLink(p.id)}
                          style={{ width: 140, padding: '3px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.8rem', outline: 'none' }} />
                        <button disabled={linking[p.id]} onClick={() => handleLink(p.id)}
                          style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: '#0D9488', color: 'white', cursor: linking[p.id] ? 'default' : 'pointer', fontSize: '0.8rem', opacity: linking[p.id] ? 0.6 : 1 }}>
                          Link
                        </button>
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <Pagination page={page} pages={data.pages} onPage={p => load(p)} />
        </>
      )}
    </div>
  );
}

// ── Tab 5: Audit Log ──────────────────────────────────────────────────────────

function AuditLogTab() {
  const [q, setQ]               = useState('');
  const [action, setAction]     = useState('');
  const [entityType, setEntityType] = useState('');
  const [page, setPage]         = useState(1);
  const [data, setData]         = useState({ logs: [], total: 0, pages: 1 });
  const [loading, setLoading]   = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback((pg = 1) => {
    setLoading(true);
    adminApi.getAuditLogs({ q, action, entity_type: entityType, page: pg })
      .then(r => { setData(r.data); setPage(pg); })
      .catch(() => toast.error('Failed to load audit logs'))
      .finally(() => setLoading(false));
  }, [q, action, entityType]);

  useEffect(() => { load(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const actionBadge = (a) => {
    const colorMap = {
      match_scored:          ['#dbeafe', '#1d4ed8'],
      draw_generated:        ['#d1fae5', '#065f46'],
      registration_opened:   ['#fef9c3', '#92400e'],
      registration_closed:   ['#fee2e2', '#b91c1c'],
      tournament_created:    ['#CCFBF1', '#6d28d9'],
    };
    const [bg, fg] = colorMap[a] || ['#f3f4f6', '#6b7280'];
    return <span style={{ background: bg, color: fg, borderRadius: 6, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{a?.replace(/_/g, ' ')}</span>;
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: '1rem', flexWrap: 'wrap' }}>
        <SearchBar value={q} onChange={setQ} placeholder="Search by user name or email…" onSearch={() => load(1)} />
        <input value={action} onChange={e => setAction(e.target.value)} placeholder="Filter by action…"
          style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem', width: 180 }} />
        <select value={entityType} onChange={e => setEntityType(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.875rem' }}>
          <option value="">All entities</option>
          <option value="tournament">Tournament</option>
          <option value="match">Match</option>
          <option value="event">Event</option>
          <option value="player">Player</option>
        </select>
        <button onClick={() => load(1)}
          style={{ padding: '7px 14px', background: '#0D9488', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: '0.875rem' }}>
          Filter
        </button>
      </div>

      {loading ? <p style={{ color: '#9ca3af' }}>Loading…</p> : (
        <>
          <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem' }}>{data.total} log{data.total !== 1 ? 's' : ''}</p>
          <TableWrap>
            <thead>
              <tr>
                <Th>Time</Th>
                <Th>User</Th>
                <Th>Action</Th>
                <Th>Entity</Th>
                <Th>Details</Th>
              </tr>
            </thead>
            <tbody>
              {data.logs.length === 0 && (
                <tr><td colSpan={5} style={{ padding: '1.5rem', textAlign: 'center', color: '#9ca3af' }}>No logs found</td></tr>
              )}
              {data.logs.map(log => (
                <tr key={log.id}>
                  <Td style={{ color: '#9ca3af', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{fmtDateTime(log.created_at)}</Td>
                  <Td style={{ fontSize: '0.8rem' }}>
                    <div style={{ fontWeight: 500 }}>{log.user_name || <span style={{ color: '#d1d5db' }}>System</span>}</div>
                    {log.user_email && <div style={{ color: '#9ca3af', fontSize: '0.72rem' }}>{log.user_email}</div>}
                  </Td>
                  <Td>{actionBadge(log.action)}</Td>
                  <Td style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                    {log.entity_type && <span style={{ textTransform: 'capitalize' }}>{log.entity_type}</span>}
                    {log.entity_id && <div style={{ color: '#d1d5db', fontSize: '0.7rem', fontFamily: 'monospace' }}>{log.entity_id.slice(0, 8)}…</div>}
                  </Td>
                  <Td>
                    {log.details ? (
                      <div>
                        <button onClick={() => setExpanded(p => ({ ...p, [log.id]: !p[log.id] }))}
                          style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 4, padding: '2px 8px', fontSize: '0.72rem', cursor: 'pointer', color: '#6b7280' }}>
                          {expanded[log.id] ? 'Hide' : 'Show'}
                        </button>
                        {expanded[log.id] && (
                          <pre style={{ marginTop: 6, fontSize: '0.72rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 10px', maxWidth: 300, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                        )}
                      </div>
                    ) : <span style={{ color: '#d1d5db' }}>—</span>}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <Pagination page={page} pages={data.pages} onPage={p => load(p)} />
        </>
      )}
    </div>
  );
}

// ── DesktopLicensesTab ────────────────────────────────────────────────────────

function DeviceList({ userId, onChanged }) {
  const [devices, setDevices] = useState(null); // null = not loaded
  const [open, setOpen]       = useState(false);
  const confirm = useConfirm();

  const load = async () => {
    try {
      const res = await adminApi.getDevicesForUser(userId);
      setDevices(res.data);
    } catch {
      toast.error('Failed to load devices');
    }
  };

  const toggle = () => {
    if (!open && devices === null) load();
    setOpen(o => !o);
  };

  const handleRevokeDevice = async (devId, devName) => {
    const ok = await confirm({ message: `Remove device "${devName || devId}"?` });
    if (!ok) return;
    try {
      await adminApi.revokeDevice(userId, devId);
      toast.success('Device removed');
      load();
      onChanged();
    } catch {
      toast.error('Failed to remove device');
    }
  };

  return (
    <div>
      <button onClick={toggle}
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: '#0D9488', padding: 0 }}>
        {open ? '▲ Hide devices' : '▼ Show devices'}
      </button>
      {open && (
        <div style={{ marginTop: 8, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.5rem 0.75rem' }}>
          {devices === null
            ? <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Loading…</span>
            : devices.length === 0
              ? <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>No devices activated yet</span>
              : devices.map(d => (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151' }}>{d.device_name || '(unnamed device)'}</div>
                    <div style={{ fontSize: '0.7rem', color: '#9ca3af', fontFamily: 'monospace' }}>{d.device_id}</div>
                    <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                      First: {fmtDateTime(d.first_seen)} · Last: {fmtDateTime(d.last_seen)}
                    </div>
                  </div>
                  <button onClick={() => handleRevokeDevice(d.id, d.device_name || d.device_id)}
                    style={{ padding: '3px 8px', background: '#fff7f7', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer', fontSize: '0.72rem', flexShrink: 0, marginLeft: 8 }}>
                    Remove
                  </button>
                </div>
              ))
          }
        </div>
      )}
    </div>
  );
}

// Format a date as YYYY-MM-DD for <input type="date"> value
function toDateInput(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toISOString().slice(0, 10);
}

function ExpiryBadge({ expiresAt }) {
  if (!expiresAt) return <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>No expiry</span>;
  const d = new Date(expiresAt);
  const expired = d < new Date();
  return (
    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: expired ? '#dc2626' : '#374151' }}>
      {expired ? '⚠ Expired ' : ''}{d.toLocaleDateString()}
    </span>
  );
}

function DesktopLicensesTab() {
  const [data, setData]           = useState({ users: [], total: 0, page: 1, pages: 1 });
  const [q, setQ]                 = useState('');
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(false);
  const [granting, setGranting]   = useState(null); // userId in grant/edit form
  const [devicesAllowed, setDevicesAllowed] = useState(1);
  const [expiresAt, setExpiresAt] = useState('');   // '' = no expiry
  const confirm = useConfirm();

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await adminApi.getDesktopLicenses({ q, page: p });
      setData(res.data);
      setPage(p);
    } catch {
      toast.error('Failed to load desktop licenses');
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openGrantForm = (u) => {
    setDevicesAllowed(u.desktop_devices_allowed || 1);
    setExpiresAt(toDateInput(u.desktop_license_expires_at));
    setGranting(u.id);
  };

  const handleGrant = async (userId) => {
    try {
      await adminApi.grantDesktopLicense(userId, {
        devices_allowed: devicesAllowed,
        expires_at: expiresAt || null,
      });
      toast.success('License granted');
      setGranting(null);
      load(page);
    } catch {
      toast.error('Failed to grant license');
    }
  };

  const handleRevoke = async (userId, userName) => {
    const ok = await confirm({ message: `Revoke desktop license for ${userName}? All activated devices will be removed.` });
    if (!ok) return;
    try {
      await adminApi.revokeDesktopLicense(userId);
      toast.success('License revoked');
      load(page);
    } catch {
      toast.error('Failed to revoke license');
    }
  };

  return (
    <div>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#111827', marginBottom: '1rem' }}>Desktop Licenses</h2>
      <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1.25rem' }}>
        Grant or revoke desktop app access for organizers. Set an optional expiry date for test or time-limited licenses.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: '1rem' }}>
        <SearchBar value={q} onChange={setQ} placeholder="Search organizers…"
          onSearch={() => load(1)} />
        <button onClick={() => load(1)}
          style={{ padding: '7px 14px', background: '#0D9488', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: '0.875rem' }}>
          Search
        </button>
      </div>

      {loading ? <p style={{ color: '#9ca3af' }}>Loading…</p> : (
        <>
          <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem' }}>{data.total} organizer{data.total !== 1 ? 's' : ''}</p>
          <TableWrap>
            <thead>
              <tr>
                <Th>Organizer</Th>
                <Th>Role</Th>
                <Th>Desktop Access</Th>
                <Th>Devices</Th>
                <Th>Expires</Th>
                <Th>Granted</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {data.users.length === 0 && (
                <tr><td colSpan={7} style={{ padding: '1.5rem', textAlign: 'center', color: '#9ca3af' }}>No organizers found</td></tr>
              )}
              {data.users.map(u => (
                <React.Fragment key={u.id}>
                  <tr>
                    <Td>
                      <div style={{ fontWeight: 500 }}>{u.name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{u.email}</div>
                    </Td>
                    <Td>
                      <span style={{ fontSize: '0.75rem', background: '#f3f4f6', padding: '2px 8px', borderRadius: 12, color: '#374151' }}>
                        {u.role}
                      </span>
                    </Td>
                    <Td>
                      {!!u.desktop_enabled
                        ? <span style={{ color: '#059669', fontWeight: 600, fontSize: '0.8rem' }}>Enabled</span>
                        : <span style={{ color: '#d1d5db', fontSize: '0.8rem' }}>—</span>}
                    </Td>
                    <Td style={{ color: '#374151', fontSize: '0.85rem' }}>
                      {u.desktop_enabled ? u.desktop_devices_allowed : '—'}
                    </Td>
                    <Td>
                      {!!u.desktop_enabled ? <ExpiryBadge expiresAt={u.desktop_license_expires_at} /> : <span style={{ color: '#d1d5db', fontSize: '0.8rem' }}>—</span>}
                    </Td>
                    <Td style={{ fontSize: '0.78rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                      {fmtDateTime(u.desktop_license_granted_at)}
                    </Td>
                    <Td>
                      {granting === u.id ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                          <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Devices:</label>
                          <input type="number" min={1} max={10} value={devicesAllowed}
                            onChange={e => setDevicesAllowed(parseInt(e.target.value) || 1)}
                            style={{ width: 48, padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem' }} />
                          <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Expires:</label>
                          <input type="date" value={expiresAt}
                            onChange={e => setExpiresAt(e.target.value)}
                            min={new Date().toISOString().slice(0, 10)}
                            style={{ padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.8rem' }} />
                          <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>(leave blank = no expiry)</span>
                          <button onClick={() => handleGrant(u.id)}
                            style={{ padding: '4px 10px', background: '#059669', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem' }}>
                            Confirm
                          </button>
                          <button onClick={() => setGranting(null)}
                            style={{ padding: '4px 10px', background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem' }}>
                            Cancel
                          </button>
                        </div>
                      ) : !!u.desktop_enabled ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => openGrantForm(u)}
                            style={{ padding: '4px 10px', background: '#f0fdf4', color: '#059669', border: '1px solid #bbf7d0', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem' }}>
                            Edit
                          </button>
                          <button onClick={() => handleRevoke(u.id, u.name)}
                            style={{ padding: '4px 10px', background: '#fff7f7', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem' }}>
                            Revoke
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => openGrantForm({ ...u, desktop_devices_allowed: 1, desktop_license_expires_at: null })}
                          style={{ padding: '4px 12px', background: '#0D9488', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem' }}>
                          Grant
                        </button>
                      )}
                    </Td>
                  </tr>
                  {!!u.desktop_enabled && (
                    <tr>
                      <td colSpan={7} style={{ padding: '0 12px 10px 12px', background: '#fafafa', borderBottom: '1px solid #f3f4f6' }}>
                        <DeviceList userId={u.id} onChanged={() => load(page)} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </TableWrap>
          <Pagination page={page} pages={data.pages} onPage={p => load(p)} />
        </>
      )}
    </div>
  );
}

// ── Main AdminPanel ───────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview',   label: 'Overview' },
  { id: 'users',      label: 'Users' },
  { id: 'tournaments',label: 'Tournaments' },
  { id: 'players',    label: 'Players' },
  { id: 'audit',      label: 'Audit Log' },
  { id: 'desktop',    label: 'Desktop Licenses' },
];

export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2rem 1rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: 0 }}>Super Admin</h1>
        <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '4px 0 0' }}>Platform management and oversight</p>
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #f0f0f0', marginBottom: '1.5rem' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{ padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600, color: activeTab === tab.id ? '#0D9488' : '#6b7280', borderBottom: activeTab === tab.id ? '2px solid #0D9488' : '2px solid transparent', marginBottom: -2, transition: 'color 0.15s' }}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview'    && <OverviewTab />}
      {activeTab === 'users'       && <UsersTab />}
      {activeTab === 'tournaments' && <TournamentsTab />}
      {activeTab === 'players'     && <PlayersTab />}
      {activeTab === 'audit'       && <AuditLogTab />}
      {activeTab === 'desktop'     && <DesktopLicensesTab />}
    </div>
  );
}
